import React from 'react';
import { useClassNameMapper, Toast } from 'react-bootstrap';
import { SharedComponent } from '../../../enums';
import Icons from '../../../icons';
import useTranslation from '../../useTranslation';
import Button from '../Button/Button';
import { ButtonVariant } from '../Button/enums';
import { IconColor, IconSize } from '../Icon/enums';
import Icon from '../Icon/Icon';
import localStyles from './ToastAlert.module.pcss';
import type Props from './ToastAlertProps';

/**
 * Toast for displaying notifications and alerts displays in one of the two variants: flyouts or banners
 *
 * @constructor
 *
 * @author Subramanian Rao, Willi Hyde
 */
const ToastAlert: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  title: TitleComponent,
  message: MessageComponent,
  toastVariant,
  alertVariant,
  autohide = false,
  delay = 5000,
  onClose,
  show,
  className,
  icon: ToastIcon = Icons.InfoIcon
}) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(SharedComponent.TOAST_ALERT);

  if (textLoading) {
    return null;
  }

  // Title can be of type string or a react functional component.
  const renderToastTitle = (): React.ReactNode => {
    return typeof TitleComponent === 'string' ? (
      <h1 className={cx('lia-title')} data-testid="ToastAlert.Title">
        {TitleComponent}
      </h1>
    ) : (
      <span className={cx('lia-title lia-is-component')} data-testid="ToastAlert.Title">
        <TitleComponent />
      </span>
    );
  };

  // Message can be of type string or a react functional component.
  const renderToastMessage = (): React.ReactNode => {
    return typeof MessageComponent === 'string' ? (
      <span className={cx('lia-message')} data-testid="ToastAlert.Message">
        {MessageComponent}
      </span>
    ) : (
      <span className={cx('lia-message lia-is-component')} data-testid="ToastAlert.Message">
        <MessageComponent />
      </span>
    );
  };

  return (
    <Toast
      show={show}
      onClose={onClose}
      autohide={autohide}
      delay={delay}
      className={cx(`lia-toast lia-${toastVariant} lia-${alertVariant}`, className)}
      data-testid={`ToastAlert.${id}`}
    >
      <Toast.Body className={cx('lia-body')}>
        <div className={cx('lia-icon-wrap')}>
          <Icon icon={ToastIcon} className={cx('lia-icon')} size={IconSize.PX_24} />
        </div>
        {renderToastTitle()}
        {renderToastMessage()}
        <Button
          variant={ButtonVariant.NO_VARIANT}
          aria-label={formatMessage('closeButton')}
          className={cx('lia-g-icon-btn lia-close')}
          onClick={onClose}
          data-testid="ToastAlert.CloseButton"
        >
          <Icon icon={Icons.CloseIcon} size={IconSize.PX_12} color={IconColor.GRAY_900} />
        </Button>
      </Toast.Body>
    </Toast>
  );
};

export default ToastAlert;
